import { useState, useEffect, useRef } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, Mail, MapPin, Globe, ChevronRight, Languages, ChevronDown, Heart, Building2, Leaf, FileText, HelpCircle, Plane, Users, Briefcase, Stethoscope } from "lucide-react";
import ConsultationModal from "@/components/ConsultationModal";
import axios from "axios";

const DEFAULT_LOGO_URL = "https://customer-assets.emergentagent.com/job_medical-compass-4/artifacts/d093vnr0_Paxgrow%20Final%20Logo.png";
const WHATSAPP_NUMBER = "917289007800";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Organized navigation with submenus
const navStructure = [
  { path: "/", label: "Home" },
  {
    label: "Services",
    submenu: [
      { path: "/services", label: "Our Services", icon: Heart, desc: "Browse medical procedures" },
      { path: "/hospitals", label: "Hospitals", icon: Building2, desc: "Our partner hospitals" },
      { path: "/doctors", label: "Doctors", icon: Stethoscope, desc: "Expert medical team" },
      { path: "/wellness", label: "Wellness & Ayurveda", icon: Leaf, desc: "Holistic healing" },
      { path: "/cost-comparison", label: "Cost Calculator", icon: FileText, desc: "Compare treatment costs" },
    ]
  },
  { path: "/treatments", label: "Treatments", icon: Heart, desc: "Browse medical procedures" }
  ,
  {
    label: "Resources",
    submenu: [
      { path: "/medical-visa", label: "Medical Visa", icon: Plane, desc: "Visa assistance & info" },
      { path: "/faq", label: "FAQ", icon: HelpCircle, desc: "Common questions" },
      { path: "/blog", label: "Health Blog", icon: FileText, desc: "Medical insights" },
    ]
  },
  { path: "/testimonials", label: "Testimonials" },
  {
    label: "Company",
    submenu: [
      { path: "/about", label: "About Us", icon: Users, desc: "Our story & mission" },
      { path: "/contact", label: "Contact", icon: Phone, desc: "Get in touch" },
      { path: "/partner-with-us", label: "Partner With Us", icon: Briefcase, desc: "Become a partner" },
      { path: "/careers", label: "Careers", icon: Users, desc: "Join our team" },
    ]
  },
];

// Flat links for mobile
const mobileNavLinks = [
  { path: "/", label: "Home" },
  { path: "/treatments", label: "Treatments" },
  { path: "/hospitals", label: "Hospitals" },
  { path: "/doctors", label: "Doctors" },
  { path: "/wellness", label: "Wellness" },
  { path: "/cost-comparison", label: "Cost Calculator" },
  { path: "/testimonials", label: "Testimonials" },
  { path: "/medical-visa", label: "Medical Visa" },
  { path: "/faq", label: "FAQ" },
  { path: "/blog", label: "Blog" },
  { path: "/about", label: "About" },
  { path: "/contact", label: "Contact" },
  { path: "/partner-with-us", label: "Partner" },
  { path: "/careers", label: "Careers" },
];

// Dropdown Menu Component
function NavDropdown({ item }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = item.submenu?.some(sub => location.pathname === sub.path);

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive
          ? "text-orange-700 bg-orange-50"
          : "text-slate-600 hover:text-orange-700 hover:bg-slate-50"
          }`}
      >
        {item.label}
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-50">
          {item.submenu.map((subItem) => {
            const Icon = subItem.icon;
            return (
              <Link
                key={subItem.path}
                to={subItem.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-start gap-3 px-4 py-3 hover:bg-orange-50 transition-colors ${location.pathname === subItem.path ? "bg-orange-50" : ""
                  }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${location.pathname === subItem.path ? "bg-orange-600 text-white" : "bg-slate-100 text-slate-600"
                  }`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <p className={`text-sm font-medium ${location.pathname === subItem.path ? "text-orange-700" : "text-slate-900"
                    }`}>{subItem.label}</p>
                  <p className="text-xs text-slate-500">{subItem.desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Google Translate Widget Component
function GoogleTranslate() {
  useEffect(() => {
    const addScript = () => {
      if (document.getElementById('google-translate-script')) return;

      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);

      window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'en,fr,sw,ar,es',
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
          },
          'google_translate_element'
        );
      };
    };

    addScript();
  }, []);

  return (
    <div id="google_translate_element" className="google-translate-wrapper" data-testid="google-translate"></div>
  );
}

function LoadingScreen() {
  const [loaded, setLoaded] = useState(false);
  const [logoSettings, setLogoSettings] = useState({ logo_url: DEFAULT_LOGO_URL, tagline: "Medical Value Travel" });

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 1500);
    axios.get(`${API}/settings/logo`).then(r => setLogoSettings(r.data)).catch(() => { });
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`loading-screen ${loaded ? "loaded" : ""}`}>
      <img src={logoSettings.logo_url || DEFAULT_LOGO_URL} alt="Paxgrow" className="loading-logo h-16 md:h-20" />
      <p className="text-sm text-orange-700 mt-3 font-body font-medium tracking-wide">{logoSettings.tagline || "Care that travels with you"}</p>
      <div className="loading-bar" />
    </div>
  );
}

// function Navbar() {
//   const location = useLocation();
//   const [mobileOpen, setMobileOpen] = useState(false);
//   const [logoSettings, setLogoSettings] = useState({
//     logo_url: DEFAULT_LOGO_URL,
//     logo_width: 64,
//     logo_height: 64,
//     tagline: "Medical Value Travel",
//     show_tagline: true
//   });

//   useEffect(() => {
//     axios.get(`${API}/settings/logo`).then(r => setLogoSettings(r.data)).catch(() => { });
//   }, []);

//   return (
//     <nav className="glass-nav sticky top-0 z-50 border-b border-slate-100" data-testid="main-navbar">
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//         <div className="flex items-center justify-between h-20 md:h-24">
//           <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
//             <div className="flex flex-col items-center">
//               <img
//                 src={logoSettings.logo_url || DEFAULT_LOGO_URL}
//                 alt="Paxgrow India - Medical Value Travel"
//                 style={{ height: logoSettings.logo_height || 64, width: 'auto' }}
//                 className="object-contain"
//               />
//               {logoSettings.show_tagline && (
//                 <p className="text-[10px] md:text-xs text-orange-600 font-semibold tracking-wide">{logoSettings.tagline}</p>
//               )}
//             </div>
//           </Link>

//           <div className="hidden xl:flex items-center gap-1">
//             {navStructure.map((item, index) => (
//               item.submenu ? (
//                 <NavDropdown key={index} item={item} />
//               ) : (
//                 <Link
//                   key={item.path}
//                   to={item.path}
//                   className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === item.path
//                       ? "text-orange-700 bg-orange-50"
//                       : "text-slate-600 hover:text-orange-700 hover:bg-slate-50"
//                     }`}
//                   data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
//                 >
//                   {item.label}
//                 </Link>
//               )
//             ))}
//           </div>

//           <div className="flex items-center gap-3">
//             <GoogleTranslate />
//             <ConsultationModal
//               trigger={
//                 <Button className="hidden sm:flex bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6 font-semibold shadow-md hover:shadow-lg transition-all" data-testid="nav-free-quote-btn">
//                   Get Free Quote
//                 </Button>
//               }
//             />
//             <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
//               <SheetTrigger asChild>
//                 <Button variant="ghost" size="icon" className="xl:hidden" data-testid="mobile-menu-btn">
//                   <Menu className="h-5 w-5" />
//                 </Button>
//               </SheetTrigger>
//               <SheetContent side="right" className="w-80 p-0">
//                 <div className="p-6">
//                   <div className="flex flex-col items-start mb-6">
//                     <img
//                       src={logoSettings.logo_url || DEFAULT_LOGO_URL}
//                       alt="Paxgrow India"
//                       style={{ height: Math.min(logoSettings.logo_height || 48, 48) }}
//                       className="mb-1 object-contain"
//                     />
//                     {logoSettings.show_tagline && (
//                       <p className="text-xs text-orange-600 font-semibold">{logoSettings.tagline}</p>
//                     )}
//                   </div>
//                   <div className="flex flex-col gap-0.5">
//                     {mobileNavLinks.map((link) => (
//                       <Link
//                         key={link.path}
//                         to={link.path}
//                         onClick={() => setMobileOpen(false)}
//                         className={`flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors ${location.pathname === link.path
//                             ? "text-orange-700 bg-orange-50"
//                             : "text-slate-600 hover:text-orange-700 hover:bg-slate-50"
//                           }`}
//                       >
//                         {link.label}
//                         <ChevronRight className="h-4 w-4" />
//                       </Link>
//                     ))}
//                   </div>
//                   <div className="mt-6">
//                     <ConsultationModal
//                       trigger={
//                         <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full font-semibold" data-testid="mobile-free-quote-btn">
//                           Get Free Quote
//                         </Button>
//                       }
//                     />
//                   </div>
//                 </div>
//               </SheetContent>
//             </Sheet>
//           </div>
//         </div>
//       </div>
//     </nav>
//   );
// }
import { FileDown } from "lucide-react"; // ← add this to your existing imports

// function Navbar() {
//   const location = useLocation();
//   const [mobileOpen, setMobileOpen] = useState(false);
//   const [logoSettings, setLogoSettings] = useState({
//     logo_url: DEFAULT_LOGO_URL,
//     logo_width: 64,
//     logo_height: 64,
//     tagline: "Medical Value Travel",
//     show_tagline: true
//   });

//   useEffect(() => {
//     axios.get(`${API}/settings/logo`).then(r => setLogoSettings(r.data)).catch(() => { });
//   }, []);

//   return (
//     <nav className="glass-nav sticky top-0 z-50 border-b border-slate-100" data-testid="main-navbar">
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//         <div className="flex items-center justify-between h-20 md:h-24">
//           <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
//             <div className="flex flex-col items-center">
//               <img
//                 src={logoSettings.logo_url || DEFAULT_LOGO_URL}
//                 alt="Paxgrow India - Medical Value Travel"
//                 style={{ height: logoSettings.logo_height || 64, width: 'auto' }}
//                 className="object-contain"
//               />
//               {logoSettings.show_tagline && (
//                 <p className="text-[10px] md:text-xs text-orange-600 font-semibold tracking-wide">{logoSettings.tagline}</p>
//               )}
//             </div>
//           </Link>

//           <div className="hidden xl:flex items-center gap-1">
//             {navStructure.map((item, index) => (
//               item.submenu ? (
//                 <NavDropdown key={index} item={item} />
//               ) : (
//                 <Link
//                   key={item.path}
//                   to={item.path}
//                   className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === item.path
//                       ? "text-orange-700 bg-orange-50"
//                       : "text-slate-600 hover:text-orange-700 hover:bg-slate-50"
//                     }`}
//                   data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
//                 >
//                   {item.label}
//                 </Link>
//               )
//             ))}
//           </div>

//           {/* ─── Right side: Google Translate → Brochure → Get Free Quote → Menu ─── */}
//           <div className="flex items-center gap-3">
//             <GoogleTranslate />

//             {/* 🆕 Download Brochure Button */}
//             <Link to="/brochure">
//               <Button
//                 variant="outline"
//                 size="sm"
//                 className="hidden sm:flex items-center gap-2 border-orange-600 text-orange-600 hover:bg-orange-50 hover:text-orange-700 rounded-full px-5 font-medium transition-all"
//                 data-testid="nav-download-brochure-btn"
//               >
//                 <FileDown className="h-4 w-4" />
//                 Brochure
//               </Button>
//             </Link>

//             <ConsultationModal
//               trigger={
//                 <Button className="hidden sm:flex bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6 font-semibold shadow-md hover:shadow-lg transition-all" data-testid="nav-free-quote-btn">
//                   Get Free Quote
//                 </Button>
//               }
//             />
//             <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
//               <SheetTrigger asChild>
//                 <Button variant="ghost" size="icon" className="xl:hidden" data-testid="mobile-menu-btn">
//                   <Menu className="h-5 w-5" />
//                 </Button>
//               </SheetTrigger>
//               <SheetContent side="right" className="w-80 p-0">
//                 <div className="p-6">
//                   <div className="flex flex-col items-start mb-6">
//                     <img
//                       src={logoSettings.logo_url || DEFAULT_LOGO_URL}
//                       alt="Paxgrow India"
//                       style={{ height: Math.min(logoSettings.logo_height || 48, 48) }}
//                       className="mb-1 object-contain"
//                     />
//                     {logoSettings.show_tagline && (
//                       <p className="text-xs text-orange-600 font-semibold">{logoSettings.tagline}</p>
//                     )}
//                   </div>
//                   <div className="flex flex-col gap-0.5">
//                     {mobileNavLinks.map((link) => (
//                       <Link
//                         key={link.path}
//                         to={link.path}
//                         onClick={() => setMobileOpen(false)}
//                         className={`flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors ${location.pathname === link.path
//                             ? "text-orange-700 bg-orange-50"
//                             : "text-slate-600 hover:text-orange-700 hover:bg-slate-50"
//                           }`}
//                       >
//                         {link.label}
//                         <ChevronRight className="h-4 w-4" />
//                       </Link>
//                     ))}
//                     {/* 🆕 Mobile brochure link */}
//                     <Link
//                       to="/brochure"
//                       onClick={() => setMobileOpen(false)}
//                       className="flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors text-orange-600 hover:bg-orange-50"
//                       data-testid="mobile-brochure-link"
//                     >
//                       <span className="flex items-center gap-2">
//                         <FileDown className="h-4 w-4" />
//                         Download Brochure
//                       </span>
//                       <ChevronRight className="h-4 w-4" />
//                     </Link>
//                   </div>
//                   <div className="mt-6">
//                     <ConsultationModal
//                       trigger={
//                         <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full font-semibold" data-testid="mobile-free-quote-btn">
//                           Get Free Quote
//                         </Button>
//                       }
//                     />
//                   </div>
//                 </div>
//               </SheetContent>
//             </Sheet>
//           </div>
//         </div>
//       </div>
//     </nav>
//   );
// }


function Navbar() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoSettings, setLogoSettings] = useState({
    logo_url: DEFAULT_LOGO_URL,
    logo_width: 64,
    logo_height: 64,
    tagline: "Medical Value Travel",
    show_tagline: true
  });

  useEffect(() => {
    axios.get(`${API}/settings/logo`).then(r => setLogoSettings(r.data)).catch(() => { });
  }, []);

  return (
    <nav className="glass-nav sticky top-0 z-50 border-b border-slate-100" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">

          {/* ─── LEFT SIDE: Mobile Menu Button (small screens) + Logo ─── */}
          <div className="flex items-center gap-2">
            {/* 🆕 Mobile Menu Button - LEFT side on small screens */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  className="xl:hidden h-12 w-12" // ✅ Larger button (48px → 48px, but with padding)
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="h-7 w-7" /> {/* ✅ Larger icon (20px → 28px) */}
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-80 p-0 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              >
                <div className="p-6">
                  <div className="flex flex-col items-start mb-6">
                    <img
                      src={logoSettings.logo_url || DEFAULT_LOGO_URL}
                      alt="Paxgrow India"
                      style={{ height: Math.min(logoSettings.logo_height || 48, 48) }}
                      className="mb-1 object-contain"
                    />
                    {logoSettings.show_tagline && (
                      <p className="text-xs text-orange-600 font-semibold">{logoSettings.tagline}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {mobileNavLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors ${location.pathname === link.path
                          ? "text-orange-700 bg-orange-50"
                          : "text-slate-600 hover:text-orange-700 hover:bg-slate-50"
                          }`}
                      >
                        {link.label}
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    ))}
                    {/* Mobile brochure link */}
                    <Link
                      to="/brochure"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors text-orange-600 hover:bg-orange-50"
                      data-testid="mobile-brochure-link"
                    >
                      <span className="flex items-center gap-2">
                        <FileDown className="h-4 w-4" />
                        Download Brochure
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                  <div className="mt-6">
                    <ConsultationModal
                      trigger={
                        <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full font-semibold" data-testid="mobile-free-quote-btn">
                          Get Free Quote
                        </Button>
                      }
                    />
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
              <div className="flex flex-col items-center">
                <img
                  src={logoSettings.logo_url || DEFAULT_LOGO_URL}
                  alt="Paxgrow India - Medical Value Travel"
                  style={{ height: logoSettings.logo_height || 64, width: 'auto' }}
                  className="object-contain"
                />
                {logoSettings.show_tagline && (
                  <p className="text-[10px] md:text-xs text-orange-600 font-semibold tracking-wide">{logoSettings.tagline}</p>
                )}
              </div>
            </Link>
          </div>

          {/* ─── CENTER: Desktop Nav Links ─── */}
          <div className="hidden xl:flex items-center gap-1">
            {navStructure.map((item, index) => (
              item.submenu ? (
                <NavDropdown key={index} item={item} />
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === item.path
                    ? "text-orange-700 bg-orange-50"
                    : "text-slate-600 hover:text-orange-700 hover:bg-slate-50"
                    }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.label}
                </Link>
              )
            ))}
          </div>

          {/* ─── RIGHT SIDE: Translate → Brochure → Quote ─── */}
          <div className="flex items-center gap-3">
            <GoogleTranslate />

            {/* Download Brochure Button (hidden on mobile) */}
            <Link to="/brochure" className="hidden sm:block">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 border-orange-600 text-orange-600 hover:bg-orange-50 hover:text-orange-700 rounded-full px-5 font-medium transition-all"
                data-testid="nav-download-brochure-btn"
              >
                <FileDown className="h-4 w-4" />
                Brochure
              </Button>
            </Link>

            <ConsultationModal
              trigger={
                <Button className="hidden sm:flex bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6 font-semibold shadow-md hover:shadow-lg transition-all" data-testid="nav-free-quote-btn">
                  Get Free Quote
                </Button>
              }
            />
          </div>
        </div>
      </div>
    </nav>
  );
}

function Footer() {
  const [logoSettings, setLogoSettings] = useState({ logo_url: DEFAULT_LOGO_URL });

  useEffect(() => {
    axios.get(`${API}/settings/logo`).then(r => setLogoSettings(r.data)).catch(() => { });
  }, []);

  return (
    <footer className="bg-slate-900 text-white" data-testid="main-footer">
      {/* WhatsApp Connect Strip */}
      <div className="bg-[#25D366] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hello%2C%20I%20would%20like%20to%20know%20more%20about%20Paxgrow%20medical%20value%20travel%20services.`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 text-white hover:opacity-90 transition-opacity"
            data-testid="footer-whatsapp-strip"
          >
            <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            <span className="font-semibold text-lg">Connect with us on WhatsApp</span>
            <span className="hidden sm:inline">+91 7289007800</span>
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <img src={logoSettings.logo_url || DEFAULT_LOGO_URL} alt="Paxgrow India" className="h-16 mb-3 brightness-0 invert" />
            <p className="text-orange-400 text-sm font-medium mb-3 italic">Care that travels with you</p>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Medical Value Travel facilitator delivering end-to-end care journeys -- from hospital excellence to holistic recovery -- built on transparency and ethical coordination.
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Globe className="h-4 w-4 text-orange-400" />
              <span>Serving 15+ countries worldwide</span>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Quick Links</h4>
            <div className="flex flex-col gap-2">
              {mobileNavLinks.slice(0, 8).map((link) => (
                <Link key={link.path} to={link.path} className="text-slate-400 hover:text-orange-400 text-sm transition-colors">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Top Treatments</h4>
            <div className="flex flex-col gap-2">
              {["Cardiology", "Orthopedics", "Dental Care", "Skin Care", "Ayurveda & Wellness", "Oncology"].map((t) => (
                <Link key={t} to="/treatments" className="text-slate-400 hover:text-orange-400 text-sm transition-colors">{t}</Link>
              ))}
            </div>
            <h4 className="font-heading text-lg font-semibold mb-4 mt-6">Resources</h4>
            <div className="flex flex-col gap-2">
              <Link to="/page/air-ambulance" className="text-slate-400 hover:text-orange-400 text-sm transition-colors">Air Ambulance</Link>
              <Link to="/page/health-insurance" className="text-slate-400 hover:text-orange-400 text-sm transition-colors">Health Insurance</Link>
              <Link to="/page/refund-policy" className="text-slate-400 hover:text-orange-400 text-sm transition-colors">Refund Policy</Link>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Contact Us</h4>
            <div className="flex flex-col gap-3">
              <a href={`tel:+${WHATSAPP_NUMBER}`} className="flex items-center gap-2 text-slate-400 hover:text-orange-400 text-sm transition-colors">
                <Phone className="h-4 w-4" /> +91 7289007800
              </a>
              <a href="mailto:hello@paxgrow.in" className="flex items-center gap-2 text-slate-400 hover:text-orange-400 text-sm transition-colors">
                <Mail className="h-4 w-4" /> paxgrowindia@gmail.com
              </a>
              <div className="flex items-start gap-2 text-slate-400 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                <span>M003 Basement Floor, Amrapali Sapphire, Sector 45, Noida 201301, UP, India</span>
              </div>
            </div>
            {/* Social Media Links */}
            <h4 className="font-heading text-lg font-semibold mt-6 mb-4">Follow Us</h4>
            <SocialMediaLinks />
          </div>
        </div>

        <div className="section-divider my-8"></div>
        <p className="text-slate-500 text-xs text-center leading-relaxed mb-4">
          We coordinate care journeys, documentation, and travel support -- while clinical decisions remain solely with the treating doctors and institutions.
        </p>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">&copy; 2026 Paxgrow India Private Limited. All rights reserved.</p>
          <div className="flex items-center gap-4 text-slate-500 text-sm">
            <Link to="/about" className="hover:text-orange-400">About</Link>
            <Link to="/careers" className="hover:text-orange-400">Careers</Link>
            <Link to="/contact" className="hover:text-orange-400">Contact</Link>
            <Link to="/partners" className="hover:text-orange-400">Partners</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Social Media Links Component
function SocialMediaLinks() {
  const [socialLinks, setSocialLinks] = useState({
    facebook: "https://facebook.com/paxgrow",
    instagram: "https://instagram.com/paxgrow",
    linkedin: "https://linkedin.com/company/paxgrow",
    twitter: "https://twitter.com/paxgrow",
    youtube: ""
  });

  useEffect(() => {
    // Fetch social links from backend
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/social`)
      .then(res => res.json())
      .then(data => {
        if (data && Object.keys(data).length > 0) {
          setSocialLinks(prev => ({ ...prev, ...data }));
        }
      })
      .catch(() => { });
  }, []);

  return (
    <div className="flex items-center gap-3">
      {socialLinks.facebook && (
        <a
          href={socialLinks.facebook}
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 rounded-full bg-slate-800 hover:bg-blue-600 flex items-center justify-center transition-colors"
          aria-label="Facebook"
        >
          <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        </a>
      )}
      {socialLinks.instagram && (
        <a
          href={socialLinks.instagram}
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 rounded-full bg-slate-800 hover:bg-gradient-to-tr hover:from-yellow-400 hover:via-pink-500 hover:to-purple-600 flex items-center justify-center transition-all"
          aria-label="Instagram"
        >
          <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
        </a>
      )}
      {socialLinks.linkedin && (
        <a
          href={socialLinks.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 rounded-full bg-slate-800 hover:bg-blue-700 flex items-center justify-center transition-colors"
          aria-label="LinkedIn"
        >
          <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
        </a>
      )}
      {socialLinks.twitter && (
        <a
          href={socialLinks.twitter}
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 rounded-full bg-slate-800 hover:bg-black flex items-center justify-center transition-colors"
          aria-label="X (Twitter)"
        >
          <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        </a>
      )}
      {socialLinks.youtube && (
        <a
          href={socialLinks.youtube}
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 rounded-full bg-slate-800 hover:bg-red-600 flex items-center justify-center transition-colors"
          aria-label="YouTube"
        >
          <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
          </svg>
        </a>
      )}
    </div>
  );
}

function WhatsAppButton() {
  return (
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hello%2C%20I%20would%20like%20to%20know%20more%20about%20Paxgrow%20medical%20value%20travel%20services.`}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-40 group"
      data-testid="whatsapp-float-btn"
      aria-label="Chat on WhatsApp"
    >
      <div className="relative">
        <div className="bg-[#25D366] text-white p-4 rounded-full shadow-xl hover:scale-110 transition-transform cursor-pointer whatsapp-pulse">
          <svg className="h-7 w-7" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </div>
        {/* Tooltip */}
        <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-white text-slate-800 px-4 py-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-sm font-medium pointer-events-none">
          Chat with us!
        </div>
      </div>
    </a>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col font-body">
      <LoadingScreen />
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
